import {getTestProps} from '@benchling/js-test-utils';
import {Box, Button, Tooltip} from '@mantine/core';
import React from 'react';
import {NodeToolbar} from 'reactflow';

import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

interface FlowchartNodeToolbarProps {
  isLocked?: boolean;
  onNodeEdit?: () => void;
  onNodeDelete?: () => void;
  /**
   * When provided, the delete button is rendered as disabled with this string shown in a tooltip
   * on hover. Use this to communicate why a node can't be deleted (e.g., tasks already exist).
   * Has no effect when {@link onNodeDelete} is not provided.
   */
  deleteDisabledTooltip?: string;
}

interface ToolbarButton {
  icon: BenchlingIconName;
  onClick: () => void;
  elementName: string;
  disabled?: boolean;
  disabledTooltip?: string;
}

export default function FlowchartNodeToolbar({
  isLocked,
  onNodeEdit,
  onNodeDelete,
  deleteDisabledTooltip,
}: FlowchartNodeToolbarProps): JSX.Element | null {
  if (!onNodeEdit && !onNodeDelete) {
    return null;
  }

  const buttons: Array<ToolbarButton> = [];

  if (onNodeEdit) {
    buttons.push({
      icon: isLocked ? 'eye' : 'edit', // When locked, the sidepanel should still open but in read only mode
      onClick: onNodeEdit,
      elementName: 'editNodeButton',
    });
  }
  if (onNodeDelete) {
    buttons.push({
      icon: 'trash-can',
      onClick: onNodeDelete,
      elementName: 'deleteNodeButton',
      disabled: deleteDisabledTooltip != null,
      disabledTooltip: deleteDisabledTooltip,
    });
  }

  return (
    <NodeToolbar {...getTestProps({component: 'FlowchartNodeToolbar', element: 'root'})}>
      <Button.Group>
        {buttons.map((button) => {
          const buttonEl = (
            <Button
              // eslint-disable-next-line no-restricted-syntax
              variant="filled"
              color="gray"
              size="md"
              compact={true}
              disabled={button.disabled}
              onClick={button.onClick}
              styles={{root: {padding: 10, width: 36, height: 36}}}
              {...getTestProps({component: 'FlowchartNodeToolbarButton', element: button.elementName})}
            >
              <BenchlingIcon icon={button.icon} />
            </Button>
          );
          if (button.disabled && button.disabledTooltip != null) {
            // Disabled buttons don't fire mouse events, so wrap in a Box for the tooltip hover target.
            return (
              <Tooltip key={button.icon} label={button.disabledTooltip}>
                <Box>{buttonEl}</Box>
              </Tooltip>
            );
          }
          return <React.Fragment key={button.icon}>{buttonEl}</React.Fragment>;
        })}
      </Button.Group>
    </NodeToolbar>
  );
}
